import { useState, useEffect, useMemo } from 'react';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BedDouble, Save, AlertTriangle, Wand2 } from 'lucide-react';
import { toast } from 'sonner';

const AREE = [
  { value: 'tutte', label: 'Tutte le aree' },
  { value: 'gialla', label: 'Area Gialla' },
  { value: 'verde', label: 'Area Verde' },
];

export default function AssegnaPostiDialog({ open, onClose, onSaved }) {
  const [ospiti, setOspiti] = useState([]);
  const [valori, setValori] = useState({});
  const [area, setArea] = useState('tutte');
  const [soloVuoti, setSoloVuoti] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [stanzaIniziale, setStanzaIniziale] = useState('');
  const [lettiPerStanza, setLettiPerStanza] = useState('2');

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get('/ospiti', { params: { stato: 'presente' } })
      .then(r => {
        setOspiti(r.data);
        const iniziali = {};
        r.data.forEach(o => { iniziali[o.id] = { stanza: o.stanza || '', letto: o.letto || '' }; });
        setValori(iniziali);
      })
      .catch(() => toast.error('Errore caricamento ospiti'))
      .finally(() => setLoading(false));
  }, [open]);

  const visibili = useMemo(() => {
    return ospiti
      .filter(o => area === 'tutte' || o.area === area)
      .filter(o => !soloVuoti || !(valori[o.id]?.stanza))
      .sort((a, b) => (a.cognome || '').localeCompare(b.cognome || ''));
  }, [ospiti, area, soloVuoti, valori]);

  const senzaPosto = ospiti.filter(o => !valori[o.id]?.stanza).length;

  const conflitti = useMemo(() => {
    const mappa = {};
    const dup = new Set();
    Object.entries(valori).forEach(([id, v]) => {
      if (!v.stanza) return;
      const chiave = `${v.stanza}|${v.letto}`;
      if (mappa[chiave]) { dup.add(id); dup.add(mappa[chiave]); }
      else mappa[chiave] = id;
    });
    return dup;
  }, [valori]);

  const setCampo = (id, campo, val) => {
    setValori(prev => ({ ...prev, [id]: { ...prev[id], [campo]: val } }));
  };

  const compilaRapido = () => {
    const start = parseInt(stanzaIniziale, 10);
    const perStanza = parseInt(lettiPerStanza, 10);
    if (!start || start < 1) { toast.error('Indica il numero della prima stanza'); return; }
    if (!perStanza || perStanza < 1) { toast.error('Indica quanti letti per stanza'); return; }

    const aggiornati = { ...valori };
    visibili.forEach((o, idx) => {
      const stanzaNum = start + Math.floor(idx / perStanza);
      const lettoIdx = (idx % perStanza) + 1;
      aggiornati[o.id] = { stanza: String(stanzaNum), letto: String(lettoIdx) };
    });
    setValori(aggiornati);
    toast.success(`Compilati ${visibili.length} ospiti da stanza ${start}. Verifica e salva.`);
  };

  const handleSave = async () => {
    if (conflitti.size > 0) { toast.error('Risolvi i posti duplicati prima di salvare'); return; }
    const assegnazioni = Object.entries(valori)
      .filter(([, v]) => v.stanza || v.letto)
      .map(([ospite_id, v]) => ({ ospite_id, stanza: v.stanza, letto: v.letto }));
    if (assegnazioni.length === 0) { toast.error('Nessuna assegnazione da salvare'); return; }

    setSaving(true);
    try {
      const res = await api.put('/ospiti/assegnazione-posti', { assegnazioni });
      toast.success(`${res.data.aggiornati} posti letto assegnati`);
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio');
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="assegna-posti-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BedDouble className="w-4 h-4 text-brand" />
            Assegnazione Posti Letto
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge className={senzaPosto > 0 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'} data-testid="senza-posto-badge">
              {senzaPosto} ospiti senza posto letto
            </Badge>
            {conflitti.size > 0 && (
              <Badge className="bg-red-100 text-red-700 flex items-center gap-1" data-testid="conflitti-badge">
                <AlertTriangle className="w-3 h-3" />{conflitti.size} posti duplicati
              </Badge>
            )}
          </div>

          <div className="flex flex-wrap items-end gap-3 p-3 rounded-lg border border-slate-200 bg-slate-50">
            <div className="space-y-1">
              <Label className="text-xs">Area</Label>
              <Select value={area} onValueChange={setArea}>
                <SelectTrigger className="w-40 h-8 text-xs" data-testid="filtro-area-posti"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {AREE.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-600 pb-1.5 cursor-pointer">
              <input type="checkbox" checked={soloVuoti} onChange={e => setSoloVuoti(e.target.checked)} className="rounded border-slate-300 w-3.5 h-3.5" data-testid="solo-vuoti-check" />
              Solo senza posto
            </label>
          </div>

          <div className="flex flex-wrap items-end gap-3 p-3 rounded-lg border border-dashed border-violet-300 bg-violet-50/50">
            <div className="space-y-1">
              <Label className="text-xs">Prima stanza</Label>
              <Input value={stanzaIniziale} onChange={e => setStanzaIniziale(e.target.value)} placeholder="es. 101" className="w-28 h-8 text-xs" data-testid="stanza-iniziale-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Letti per stanza</Label>
              <Input value={lettiPerStanza} onChange={e => setLettiPerStanza(e.target.value)} className="w-24 h-8 text-xs" data-testid="letti-per-stanza-input" />
            </div>
            <Button size="sm" variant="outline" onClick={compilaRapido} className="h-8 text-xs border-violet-300 text-violet-700 hover:bg-violet-100" data-testid="compila-rapido-btn">
              <Wand2 className="w-3 h-3 mr-1" />Compila in sequenza
            </Button>
            <p className="text-[10px] text-slate-500 flex-1 min-w-[200px]">
              Numera progressivamente gli ospiti visibili. Puoi correggere ogni riga prima di salvare.
            </p>
          </div>

          {loading ? (
            <p className="text-center py-8 text-slate-400 text-sm">Caricamento...</p>
          ) : visibili.length === 0 ? (
            <p className="text-center py-8 text-slate-400 text-sm">Nessun ospite da assegnare con questi filtri.</p>
          ) : (
            <div className="space-y-1.5">
              {visibili.map(o => {
                const inConflitto = conflitti.has(o.id);
                return (
                  <div key={o.id} className={`flex items-center gap-2 p-2 rounded-lg border ${inConflitto ? 'border-red-300 bg-red-50' : 'border-slate-200'}`} data-testid={`riga-posto-${o.id}`}>
                    <span className="text-xs font-medium text-slate-700 flex-1 truncate">
                      {o.cognome} {o.nome}
                      {o.area && <Badge variant="outline" className="ml-1.5 text-[9px]">{o.area}</Badge>}
                    </span>
                    <Input
                      value={valori[o.id]?.stanza || ''}
                      onChange={e => setCampo(o.id, 'stanza', e.target.value)}
                      placeholder="Stanza"
                      className="w-24 h-8 text-xs"
                      data-testid={`stanza-input-${o.id}`}
                    />
                    <Input
                      value={valori[o.id]?.letto || ''}
                      onChange={e => setCampo(o.id, 'letto', e.target.value)}
                      placeholder="Letto"
                      className="w-20 h-8 text-xs"
                      data-testid={`letto-input-${o.id}`}
                    />
                  </div>
                );
              })}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <Button variant="outline" size="sm" onClick={onClose}>Annulla</Button>
            <Button size="sm" onClick={handleSave} disabled={saving || conflitti.size > 0} className="bg-brand hover:bg-brand-dark text-white" data-testid="salva-posti-btn">
              <Save className="w-3.5 h-3.5 mr-1" />{saving ? 'Salvataggio...' : 'Salva Assegnazioni'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
